/**
 * Shared Top Picks product card swatch + add-to-cart logic.
 * Used by sections/top-picks-for-you.liquid and sections/baby-stage-recommendations.liquid.
 */
(function (global) {
  function readConfig(root) {
    let moneyFormat = null;
    let moneyFormatExample = null;
    const cfgEl = root.querySelector('[data-top-picks-config]');
    if (cfgEl && cfgEl.textContent) {
      try {
        const cfg = JSON.parse(cfgEl.textContent);
        if (cfg.moneyFormat) moneyFormat = cfg.moneyFormat;
        if (cfg.moneyFormatExample) moneyFormatExample = cfg.moneyFormatExample;
      } catch (e) {}
    }
    let rootUrl = root.getAttribute('data-root-url') || '/';
    if (rootUrl.slice(-1) !== '/') rootUrl += '/';
    return { moneyFormat, moneyFormatExample, rootUrl };
  }

  function createHelpers(moneyFormat, moneyFormatExample, rootUrl) {
    function formatPrice(cents) {
      const n = Number(cents);
      if (!Number.isFinite(n) || n < 0) return '';
      if (n === 0) return '';
      const safe = Math.round(n);
      if (
        typeof global.formatMoneyLatest === 'function' &&
        moneyFormat &&
        moneyFormatExample
      ) {
        const out = global.formatMoneyLatest(moneyFormat, moneyFormatExample, safe);
        return typeof out === 'string' ? out.replace('.00', '') : String(out);
      }
      return '$' + (safe / 100).toFixed(2).replace(/\.00$/, '');
    }

    function optNorm(v) {
      if (v == null) return '';
      return String(v).trim();
    }

    function toCents(value) {
      if (value == null) return 0;
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const n = parseInt(String(value).replace(/[^\d-]/g, ''), 10);
      return Number.isFinite(n) ? n : 0;
    }

    function sleep(ms) {
      return new Promise((r) => setTimeout(r, ms));
    }

    async function cartAddWithRetry(variantId) {
      const maxAttempts = 3;
      const delays = [0, 1200, 2500];
      let lastStatus = 0;
      let lastPayload = null;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (delays[attempt]) await sleep(delays[attempt]);
        const res = await fetch(rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        lastStatus = res.status;
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        let payload = null;
        if (ct.includes('application/json')) {
          try {
            payload = await res.json();
          } catch (_) {
            payload = null;
          }
        } else {
          await res.text().catch(() => {});
        }
        lastPayload = payload;
        if (res.ok) return { ok: true, payload };
        if (res.status === 429 && attempt < maxAttempts - 1) continue;
        return { ok: false, status: res.status, payload };
      }
      return { ok: false, status: lastStatus, payload: lastPayload };
    }

    return { formatPrice, optNorm, toCents, cartAddWithRetry, rootUrl };
  }

  function bindTopPicksCard(card, helpers) {
    const { formatPrice, optNorm, toCents, cartAddWithRetry, rootUrl } = helpers;

    if (!card.dataset.topPicksSwatchBound) {
      card.dataset.topPicksSwatchBound = '1';

      const isDual =
        card.getAttribute('data-top-picks-dual') === 'true' ||
        !!card.querySelector('[data-top-picks-swatch][data-option-idx]');

      let dualVariants = [];
      let dualO1 = '';
      let dualO2 = '';
      if (isDual) {
        dualVariants = loadDualVariants(card);
        dualO1 = optNorm(card.getAttribute('data-selected-o1'));
        dualO2 = optNorm(card.getAttribute('data-selected-o2'));
        if (dualVariants.length) updateDualRow2Disabled(card, dualVariants, dualO1, optNorm);
      }

      card.addEventListener('click', function (ev) {
        const sw = ev.target.closest('[data-top-picks-swatch]');
        if (!sw || !card.contains(sw) || sw.disabled) return;

        const optionIdxAttr = sw.getAttribute('data-option-idx');
        const isDualSw = optionIdxAttr !== null && optionIdxAttr !== '';

        if (isDualSw) {
          if (!dualVariants.length) return;
          const idx = parseInt(optionIdxAttr, 10);
          const val = optNorm(sw.getAttribute('data-option-value'));
          if (!val) return;
          if (idx === 0) {
            dualO1 = val;
            if (!findDualVariant(dualVariants, dualO1, dualO2, optNorm)) {
              dualO2 = firstVisibleComboForO1(dualVariants, dualO1, optNorm) || '';
            }
          } else if (idx === 1) {
            dualO2 = val;
          }
          if (dualO1 && dualO2) {
            const next = applyDualSelection(
              card,
              dualVariants,
              dualO1,
              dualO2,
              helpers,
              optNorm
            );
            if (next) {
              dualO1 = next.o1;
              dualO2 = next.o2;
            }
          }
          return;
        }

        const vid = parseInt(sw.getAttribute('data-variant-id'), 10);
        if (!Number.isFinite(vid)) return;
        const imgSrc = sw.getAttribute('data-img-src') || '';
        const imgAlt = sw.getAttribute('data-img-alt') || '';
        const priceCents = toCents(sw.getAttribute('data-price'));
        const compareCents = toCents(sw.getAttribute('data-compare-at-price'));
        setActiveSwatch(card, sw);
        applyVariant(card, vid, imgSrc, imgAlt, priceCents, compareCents, formatPrice, toCents);
      });
    }

    const addBtn = card.querySelector('[data-top-picks-add]');
    if (addBtn && !addBtn.dataset.topPicksBound) {
      addBtn.dataset.topPicksBound = '1';
      addBtn.addEventListener(
        'click',
        async (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

          const variantId = parseInt(card.getAttribute('data-selected-variant-id'), 10);
          const handle = card.getAttribute('data-product-handle');
          if (!variantId) return;
          if (addBtn.dataset.topPicksLoading === '1') return;
          addBtn.dataset.topPicksLoading = '1';
          addBtn.classList.add('top-picks-card__heart--loading');

          try {
            const result = await cartAddWithRetry(variantId);
            if (!result.ok) {
              const reason =
                (result.payload && (result.payload.description || result.payload.message)) ||
                'HTTP ' + (result.status || '?');
              console.warn(
                '[top-picks] add to cart rejected',
                { handle: handle, variantId: variantId, status: result.status, reason: reason },
                result.payload
              );
              throw new Error('Cart add failed: ' + reason);
            }
            addBtn.classList.add('top-picks-card__heart--added');
            addBtn.setAttribute('aria-label', 'Added to cart');
            document.dispatchEvent(new CustomEvent('stylux:cart:refresh'));
            document.dispatchEvent(new CustomEvent('cart:updated'));
          } catch (err) {
            addBtn.classList.remove('top-picks-card__heart--added');
            addBtn.classList.add('top-picks-card__heart--error');
            setTimeout(() => addBtn.classList.remove('top-picks-card__heart--error'), 1400);
          } finally {
            delete addBtn.dataset.topPicksLoading;
            addBtn.classList.remove('top-picks-card__heart--loading');
          }
        },
        true
      );
    }

    bindQuickShop(card, rootUrl);
  }

  /**
   * Quick Shop modal — same behavior as Find Your Perfect Fit (frc-quick-shop).
   */
  function bindQuickShop(card, rootUrl) {
    const host = card.querySelector('[data-frc-card]') || card;
    if (host.dataset.frcBound === '1') return;

    const modal = host.querySelector('[data-frc-modal]');
    const trigger = host.querySelector('[data-frc-quick-shop]');
    if (!modal || !trigger) return;
    host.dataset.frcBound = '1';

    if (!modal.dataset.frcPortaled) {
      document.body.appendChild(modal);
      modal.dataset.frcPortaled = '1';
    }

    const dialog = modal.querySelector('.frc-quick-shop__dialog');
    const imageEl = modal.querySelector('[data-frc-modal-image]');
    const priceEl = modal.querySelector('[data-frc-modal-price]');
    const compareEl = modal.querySelector('[data-frc-modal-compare]');
    const errorEl = modal.querySelector('[data-frc-modal-error]');
    const modalAddBtn = modal.querySelector('[data-frc-modal-add]');
    const swatchEls = Array.prototype.slice.call(modal.querySelectorAll('[data-frc-swatch]'));
    const groupValueEls = Array.prototype.slice.call(modal.querySelectorAll('[data-frc-group-value]'));

    let variants = [];
    const scriptEl = modal.querySelector('[data-frc-variants]');
    if (scriptEl) {
      try {
        variants = JSON.parse(scriptEl.textContent);
      } catch (err) {
        variants = [];
      }
    }

    let selected = [];
    const optionIndices = [];
    swatchEls.forEach(function (btn) {
      const i = parseInt(btn.getAttribute('data-option-idx'), 10);
      if (optionIndices.indexOf(i) === -1) optionIndices.push(i);
    });
    optionIndices.sort(function (a, b) {
      return a - b;
    });

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    function findVariant(values) {
      for (let i = 0; i < variants.length; i++) {
        const opts = variants[i].options || [];
        let match = true;
        for (let k = 0; k < optionIndices.length; k++) {
          const oi = optionIndices[k];
          if (optNormLocal(opts[oi]) !== optNormLocal(values[oi])) {
            match = false;
            break;
          }
        }
        if (match) return variants[i];
      }
      return null;
    }

    function optNormLocal(value) {
      return value == null ? '' : String(value).trim();
    }

    function getSelectedVariant() {
      return findVariant(selected);
    }

    function matchesPrior(variant, idx) {
      const opts = variant.options || [];
      for (let k = 0; k < optionIndices.length; k++) {
        const oi = optionIndices[k];
        if (oi >= idx) break;
        if (selected[oi] != null && optNormLocal(opts[oi]) !== optNormLocal(selected[oi])) {
          return false;
        }
      }
      return true;
    }

    function valueExists(idx, value, requireAvailable) {
      return variants.some(function (v) {
        if (!matchesPrior(v, idx)) return false;
        if (optNormLocal((v.options || [])[idx]) !== optNormLocal(value)) return false;
        return requireAvailable ? !!v.available : true;
      });
    }

    function firstValidValue(idx) {
      let anyValue = null;
      for (let i = 0; i < variants.length; i++) {
        const v = variants[i];
        if (!matchesPrior(v, idx)) continue;
        const val = (v.options || [])[idx];
        if (anyValue == null) anyValue = val;
        if (v.available) return val;
      }
      return anyValue;
    }

    function displayNameFor(idx, value) {
      for (let i = 0; i < swatchEls.length; i++) {
        const btn = swatchEls[i];
        if (parseInt(btn.getAttribute('data-option-idx'), 10) !== idx) continue;
        if (optNormLocal(btn.getAttribute('data-option-value')) !== optNormLocal(value)) continue;
        return btn.getAttribute('data-display-value') || value || '';
      }
      return value || '';
    }

    function render() {
      const variant = getSelectedVariant();

      if (variant) {
        if (priceEl) {
          priceEl.textContent = variant.price || '';
          priceEl.classList.toggle('frc-quick-shop__price--sale', !!variant.compare);
        }
        if (compareEl) compareEl.textContent = variant.compare || '';
        if (variant.image && imageEl) imageEl.src = variant.image;
        if (modalAddBtn) {
          modalAddBtn.disabled = !variant.available;
          modalAddBtn.textContent = variant.available ? 'Add to Cart' : 'Out of Stock';
        }
        card.setAttribute('data-selected-variant-id', String(variant.id));
      } else if (modalAddBtn) {
        modalAddBtn.disabled = true;
        modalAddBtn.textContent = 'Unavailable';
      }

      groupValueEls.forEach(function (el) {
        const idx = parseInt(el.getAttribute('data-frc-group-value'), 10);
        el.textContent = displayNameFor(idx, selected[idx]);
      });

      swatchEls.forEach(function (btn) {
        const idx = parseInt(btn.getAttribute('data-option-idx'), 10);
        const value = btn.getAttribute('data-option-value');
        const active = optNormLocal(selected[idx]) === optNormLocal(value);
        btn.classList.toggle('frc-quick-shop__swatch--active', active);
        btn.setAttribute('aria-checked', active ? 'true' : 'false');

        const exists = valueExists(idx, value, false);
        const hasStock = exists && valueExists(idx, value, true);
        btn.classList.toggle('frc-quick-shop__swatch--disabled', !exists);
        btn.classList.toggle('frc-quick-shop__swatch--sold-out', exists && !hasStock);
        if (!exists) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    }

    function selectValue(idx, value) {
      clearError();
      if (!valueExists(idx, value, false)) return;
      selected[idx] = value;
      for (let k = 0; k < optionIndices.length; k++) {
        const oi = optionIndices[k];
        if (oi <= idx) continue;
        if (!valueExists(oi, selected[oi], false)) {
          selected[oi] = firstValidValue(oi);
        }
      }
      render();
    }

    function initSelection() {
      let first = null;
      for (let i = 0; i < variants.length; i++) {
        if (variants[i].available) {
          first = variants[i];
          break;
        }
      }
      if (!first) first = variants[0];
      selected = first && first.options ? first.options.slice() : [];
    }

    function closeAllModals() {
      document.querySelectorAll('[data-frc-modal]').forEach(function (m) {
        m.classList.remove('is-open');
        m.setAttribute('aria-hidden', 'true');
        m.hidden = true;
      });
      document.body.classList.remove('frc-quick-shop-open');
    }

    function openModal() {
      clearError();
      initSelection();
      render();
      closeAllModals();
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('frc-quick-shop-open');
      requestAnimationFrame(function () {
        modal.classList.add('is-open');
      });
      if (dialog) dialog.focus();
    }

    function closeModal() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('frc-quick-shop-open');
      window.setTimeout(function () {
        modal.hidden = true;
      }, 250);
      if (trigger && typeof trigger.focus === 'function') trigger.focus();
    }

    async function addToCart() {
      const variant = getSelectedVariant();
      if (!variant || !variant.available || !modalAddBtn) return;
      clearError();
      modalAddBtn.classList.add('is-loading');
      modalAddBtn.disabled = true;
      const previousLabel = modalAddBtn.textContent;
      modalAddBtn.textContent = 'Adding...';

      try {
        const res = await fetch(rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
        });
        if (!res.ok) {
          const payload = await res.json().catch(function () {
            return {};
          });
          throw new Error(payload.description || payload.message || 'Add to cart failed');
        }
        document.dispatchEvent(new CustomEvent('stylux:cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
        closeModal();
      } catch (err) {
        showError(err.message || 'Something went wrong. Please try again.');
        modalAddBtn.textContent = previousLabel;
      } finally {
        modalAddBtn.classList.remove('is-loading');
        modalAddBtn.disabled = false;
      }
    }

    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      if (!variants.length) return;
      openModal();
    });

    swatchEls.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.classList.contains('frc-quick-shop__swatch--disabled')) return;
        const idx = parseInt(btn.getAttribute('data-option-idx'), 10);
        selectValue(idx, btn.getAttribute('data-option-value'));
      });
    });

    modal.querySelectorAll('[data-frc-close]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    if (modalAddBtn) modalAddBtn.addEventListener('click', addToCart);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });
  }

  function setActiveSwatch(card, btnEl) {
    card.querySelectorAll('[data-top-picks-swatch]').forEach((b) => {
      b.classList.toggle('top-picks-card__swatch--active', b === btnEl);
      b.setAttribute('aria-pressed', b === btnEl ? 'true' : 'false');
    });
  }

  function setDualSwatchActive(card, o1, o2, optNorm) {
    const a1 = optNorm(o1);
    const a2 = optNorm(o2);
    card.querySelectorAll('[data-top-picks-swatch]').forEach((b) => {
      const idx = b.getAttribute('data-option-idx');
      const val = optNorm(b.getAttribute('data-option-value'));
      const active = (idx === '0' && val === a1) || (idx === '1' && val === a2);
      b.classList.toggle('top-picks-card__swatch--active', active);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function findDualVariant(variants, o1, o2, optNorm) {
    const a1 = optNorm(o1);
    const a2 = optNorm(o2);
    return (
      variants.find((x) => optNorm(x.option1) === a1 && optNorm(x.option2) === a2) || null
    );
  }

  function firstVisibleComboForO1(variants, o1, optNorm) {
    const a1 = optNorm(o1);
    const row = variants.find((x) => optNorm(x.option1) === a1);
    return row ? optNorm(row.option2) : '';
  }

  function updateDualRow2Disabled(card, variants, o1, optNorm) {
    const a1 = optNorm(o1);
    card.querySelectorAll('[data-top-picks-swatch][data-option-idx="1"]').forEach((b) => {
      const v2 = optNorm(b.getAttribute('data-option-value'));
      const ok = variants.some((x) => optNorm(x.option1) === a1 && optNorm(x.option2) === v2);
      b.classList.toggle('top-picks-card__swatch--disabled', !ok);
      b.disabled = !ok;
      if (!ok) b.setAttribute('aria-disabled', 'true');
      else b.removeAttribute('aria-disabled');
    });
  }

  function loadDualVariants(card) {
    const el =
      card.querySelector('script[type="application/json"][data-top-picks-variant-map]') ||
      card.querySelector('[data-top-picks-variant-map]');
    if (!el || !el.textContent) return [];
    try {
      const parsed = JSON.parse(el.textContent.trim());
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function variantRowImageUrl(v) {
    if (!v) return '';
    if (typeof v.image === 'string' && v.image) return v.image;
    const fi = v.featured_image;
    if (!fi) return '';
    if (typeof fi === 'string') return fi;
    if (fi.src) return fi.src;
    if (fi.url) return fi.url;
    return '';
  }

  function variantRowImageAlt(v, fallback) {
    if (!v) return fallback || '';
    if (v.image_alt) return String(v.image_alt);
    const fi = v.featured_image;
    if (fi && typeof fi === 'object' && fi.alt) return String(fi.alt);
    return fallback || '';
  }

  function applyVariant(card, variantId, imgSrc, imgAlt, priceCents, compareAtCents, formatPrice, toCents) {
    if (variantId != null && variantId !== '') {
      card.setAttribute('data-selected-variant-id', String(variantId));
    }
    const img = card.querySelector('.top-picks-card__img');
    if (img && imgSrc) {
      img.src = imgSrc;
      if (imgAlt) img.alt = imgAlt;
    }
    const price = toCents(priceCents);
    const compare = toCents(compareAtCents);
    const onSale = compare > 0 && compare > price;
    const priceEl = card.querySelector('[data-top-picks-price]');
    const compareEl = card.querySelector('[data-top-picks-compare]');
    if (priceEl) {
      priceEl.textContent = formatPrice(price);
      priceEl.classList.toggle('top-picks-card__price--active', onSale);
    }
    if (compareEl) {
      compareEl.textContent = onSale ? formatPrice(compare) : '';
      compareEl.classList.toggle('top-picks-card__compare--active', onSale);
    }
  }

  function applyDualSelection(card, variants, o1, o2, helpers, optNorm) {
    let v = findDualVariant(variants, o1, o2, optNorm);
    if (!v) {
      const o2n = firstVisibleComboForO1(variants, o1, optNorm);
      if (!o2n) return null;
      o2 = o2n;
      v = findDualVariant(variants, o1, o2, optNorm);
    }
    if (!v) return null;
    const imgUrl = variantRowImageUrl(v);
    const imgAlt = variantRowImageAlt(v, '');
    applyVariant(
      card,
      v.id,
      imgUrl,
      imgAlt,
      helpers.toCents(v.price),
      helpers.toCents(v.compare_at_price),
      helpers.formatPrice,
      helpers.toCents
    );
    card.setAttribute('data-selected-o1', optNorm(o1));
    card.setAttribute('data-selected-o2', optNorm(o2));
    setDualSwatchActive(card, o1, o2, optNorm);
    updateDualRow2Disabled(card, variants, o1, optNorm);
    return { o1: optNorm(o1), o2: optNorm(o2) };
  }

  function initTopPicksCards(root) {
    if (!root || root.dataset.topPicksCardsInit === '1') return;
    root.dataset.topPicksCardsInit = '1';

    const config = readConfig(root);
    const helpers = createHelpers(config.moneyFormat, config.moneyFormatExample, config.rootUrl);

    root.querySelectorAll('[data-top-picks-card]').forEach((card) => {
      try {
        bindTopPicksCard(card, helpers);
      } catch (err) {
        console.error('[top-picks] bindTopPicksCard failed', err);
      }
    });
  }

  function initAllTopPicksRoots() {
    document.querySelectorAll('[data-top-picks-root]').forEach(initTopPicksCards);
  }

  global.initTopPicksCards = initTopPicksCards;
  global.bindTopPicksCard = function (card, root) {
    if (!card || !root) return;
    const config = readConfig(root);
    const helpers = createHelpers(config.moneyFormat, config.moneyFormatExample, config.rootUrl);
    bindTopPicksCard(card, helpers);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllTopPicksRoots);
  } else {
    initAllTopPicksRoots();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    const sectionRoot = document.querySelector(
      '#shopify-section-' + ev.detail.sectionId + ' [data-top-picks-root]'
    );
    if (sectionRoot) initTopPicksCards(sectionRoot);
  });
})(window);
